import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import MyMessagesProvider from "./MyMessagesProvider";
import ShowToast from "../../../widget/common/ui/Toast";
import AppTranslation from "../../../service/app_translation/AppTranslation";
import UtilityHelper from "../../../service/utilities/UtilityHelper";

import { MenuMyMessagesList } from "../../../model/work_profile/MyMessagesModel";
import { AlertConfirm, AlertMesage } from "../../../widget/common/ui/Alerts";
import Loader from "../../../widget/common/ui/Loader";
import { useDispatch } from "react-redux";
import { sendChatMessage } from "../../../libraries/state_reducers/chats/ChatsReducer";
import { removeModal } from "../../../libraries/state_reducers/widget/ModalsReducer";
import ModalKeys from "../../../const/ModalKeys";

type MyMessagesTypes = {
  maxWords: number;
  submitForm: boolean;
  tabName: "none" | "delete" | "add";
  titleField: string;
  messageField: string;
  loadedStatus: boolean;

  selectedIds: Array<string>;
  messageList: Array<MenuMyMessagesList>;

  selectedId: string;
  fieldsValid: FormValidType;

  handleTab: (name: "none" | "delete" | "add") => void;
  handleItemDelete: (id: string) => void;

  handleDeleteSubmit: () => void;
  handleDeleteCancel: () => void;

  handleSubmit: () => void;
  handleClear: () => void;

  handleClickMessage: (detail: MenuMyMessagesList) => void;
  handleOnChange: (
    name: string,
    value: string,
    isValid?: boolean | undefined
  ) => void;

  handleClickToggleEdit: (id: string) => void;
  handleSendMessage: (msg: string) => void;
  handleSendMessageOnceOff: () => void;
};

type FormValidType = {
  title: boolean;
  message: boolean;
};

const MyMessagesContext = createContext<MyMessagesTypes | undefined>(undefined);

export const MyMessagesContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const maxWords = 2000;

  const dispatch = useDispatch();

  const fieldsValidDefault: FormValidType = {
    title: false,
    message: false,
  };

  const fieldsValidRef = useRef<FormValidType>(fieldsValidDefault);
  const titleCurrentRef = useRef<string>("");
  const messageCurrentRef = useRef<string>("");

  const [selectedId, setSelectedId] = useState<string>("");
  const [loadedStatus, setLoadedStatus] = useState<boolean>(false);
  const [submitForm, setSubmitForm] = useState<boolean>(false);
  const [fieldsValid, setFieldsValid] =
    useState<FormValidType>(fieldsValidDefault);
  const [tabName, setTabName] = useState<"none" | "delete" | "add">("none");
  const [formButton, setFormButton] = useState<"change" | "save" | "update">(
    "save"
  );

  const [messageList, setMssageList] = useState<Array<MenuMyMessagesList>>([]);
  const [titleField, setTitleField] = useState<string>("");
  const [messageField, setMessageField] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);

  const resetForm = (loaded: boolean = false) => {
    fieldsValidRef.current = fieldsValidDefault;
    messageCurrentRef.current = "";
    titleCurrentRef.current = "";

    setLoadedStatus(loaded);
    setTitleField("");
    setMessageField("");
    setSelectedIds([]);
    setSubmitForm(false);
    setFormButton("save");
    setTabName("none");

    setFieldsValid(fieldsValidDefault);
    setSelectedId("");
  };

  const handleOnChange = (
    fieldName: string,
    fieldValue: string,
    isValid?: boolean | undefined
  ) => {
    fieldName = fieldName.toLowerCase();

    if (fieldName === "title") {
      setTitleField(fieldValue);
      fieldsValidRef.current.title = isValid ?? false;
      setFieldsValid({
        ...fieldsValid,
        title: fieldsValidRef.current.title,
      });
    } else if (fieldName === "message") {
      setMessageField(fieldValue);
      fieldsValidRef.current.message = isValid ?? false;
      setFieldsValid({
        ...fieldsValid,
        message: fieldsValidRef.current.message,
      });
    }

    setSubmitForm(false);
  };

  const handleItemDelete = (id: string) => {
    let ids = [...selectedIds];

    if (ids.includes(id)) {
      ids = ids.filter((item) => item !== id);
    } else {
      ids.push(id);
    }
    setSelectedIds(ids);
  };

  const handleSubmit = async () => {
    setSubmitForm(true);

    if (fieldsValidRef.current.message && fieldsValidRef.current.title) {
      if (formButton === "save") {
        manageAPI("save");
      } else if (formButton === "update") {
        if (
          titleCurrentRef.current.toLowerCase() === titleField.toLowerCase() &&
          messageCurrentRef.current.toLowerCase() === messageField.toLowerCase()
        ) {
          aletMessage("same");
        } else {
          manageAPI("update");
        }
      }
    } else {
      if (!fieldsValidRef.current.message) {
        if (UtilityHelper.countWords(messageField) > maxWords) {
          ShowToast.warning(AppTranslation.text("character_exceeded"));
        }
      }
    }
  };

  const manageAPI = async (action: "save" | "update") => {
    Loader.modal();
    const menuMyMessagesList: Array<MenuMyMessagesList> | null =
      await MyMessagesProvider.manageAPI({
        group_ids: [action === "save" ? "0" : "1"],
        title: titleField,
        message: messageField,
        flag: action === "save" ? "1" : "2",
      });
    Loader.modal(false);

    if (menuMyMessagesList != null) {
      setMssageList(menuMyMessagesList);
      aletMessage(action);
    }
  };

  const aletMessage = (status: "save" | "update" | "same" = "save") => {
    AlertMesage({
      title: ["same", "update"].includes(status)
        ? "message_updated_successfully"
        : "message_created_successfully",
      onClick: () => {
        resetForm(true);
      },
    });
  };

  const handleClear = () => {
    setMessageField("");
    fieldsValidRef.current = fieldsValidDefault;
  };

  const handleClickToggleEdit = (id: string) => {
    setSelectedId(id === selectedId ? "" : id);
  };

  const handleClickMessage = (detail: MenuMyMessagesList) => {
    fieldsValidRef.current = fieldsValidDefault;
    messageCurrentRef.current = detail?.title!;
    titleCurrentRef.current = detail?.message!;

    setTitleField(detail?.title!);
    setMessageField(detail?.message!);

    setSubmitForm(false);
    setFormButton("change");
    setTabName("add");
  };

  const handleTab = (name: "none" | "delete" | "add") => {
    setTabName(name);
    setSelectedId("");

    if (name === "delete") {
      setSelectedIds([]);
    } else if (name === "add") {
      fieldsValidRef.current = fieldsValidDefault;
      messageCurrentRef.current = "";
      titleCurrentRef.current = "";

      setTitleField("");
      setMessageField("");
      setSubmitForm(false);
    }
  };

  const handleDeleteSubmit = () => {
    if (selectedIds.length > 0) {
      AlertConfirm({
        title: "are_you_sure_wish_delete_messages",
        onConfirm: async () => {
          deleteAPI();
        },
      });
    }
  };

  const handleDeleteCancel = () => {
    resetForm(true);
  };

  const deleteAPI = async () => {
    resetForm(true);
    Loader.modal();
    const menuMyMessagesList: Array<MenuMyMessagesList> | null =
      await MyMessagesProvider.manageAPI({
        group_ids: selectedIds,
        flag: "0",
      });
    Loader.modal(false);

    if (menuMyMessagesList != null) {
      setMssageList(menuMyMessagesList);
    }
  };

  const getMessagesAPI = async () => {
    resetForm();
    setLoadedStatus(false);

    const menuMyMessagesList: Array<MenuMyMessagesList> | null =
      await MyMessagesProvider.getMessagesAPI();
    setLoadedStatus(true);

    if (menuMyMessagesList != null) {
      setMssageList(menuMyMessagesList);
    }
  };

  const handleSendMessage = (msg: string) => {
    if (UtilityHelper.isNotEmpty(msg)) {
      dispatch(sendChatMessage({ messageType: "1", message: msg }));
      dispatch(removeModal(ModalKeys.chat_work_my_messages));
    }
  };

  const handleSendMessageOnceOff = () => {
    if (UtilityHelper.isNotEmpty(messageField)) {
      handleSendMessage(messageField);
    }
  };

  useEffect(() => {
    getMessagesAPI();
    // eslint-disable-next-line
  }, []);

  return (
    <MyMessagesContext.Provider
      value={{
        submitForm,
        tabName,
        maxWords,
        titleField,
        messageField,
        loadedStatus,

        selectedIds,
        selectedId,
        messageList,

        fieldsValid,

        handleTab,
        handleItemDelete,

        handleSubmit,
        handleOnChange,
        handleClear,

        handleDeleteSubmit,
        handleDeleteCancel,

        handleClickMessage,

        handleClickToggleEdit,

        handleSendMessage,
        handleSendMessageOnceOff,
      }}
    >
      {children}
    </MyMessagesContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const MyMessagesContextImport = () => {
  const context = useContext(MyMessagesContext);
  if (!context) {
    throw new Error(
      "MyMessagesContextImport must be used within a MyMessagesContextProvider"
    );
  }
  return context;
};
